import React, { useMemo } from 'react';
import CharacterPrintDocument, { CharacterPrintPortal } from '../components/character-print/CharacterPrintDocument';
import { buildCharacterPrintModel } from '../utils/CharacterPrintPresentation';
import { buildCharacterActionEconomyViewModel } from '../utils/CharacterActionEconomyPresentation';

const LONG_ITEM_DESCRIPTION = 'A field-tested item with enough rules text to exercise wrapping, continuation pages, and the readable detail layout without depending on an interactive drawer.';

const EQUIPPED_STRESS_ITEMS = [
  ['Shadow Hood', 'adventuring gear'],
  ['Studded Leather Armor', 'armor'],
  ['Lantern Network Cloak', 'adventuring gear'],
  ['Clockwork Lockpick Bracer', 'tool'],
  ['Night Key Utility Belt', 'adventuring gear'],
  ['Softstep Boots', 'adventuring gear']
];

const equipmentItems = Array.from({ length: 62 }, (_, index) => ({
  name: EQUIPPED_STRESS_ITEMS[index]?.[0] || `Field Kit Item ${String(index + 1).padStart(2, '0')}`,
  type: EQUIPPED_STRESS_ITEMS[index]?.[1] || (index % 7 === 0 ? 'tool' : index % 5 === 0 ? 'consumable' : 'adventuring gear'),
  quantity: (index % 4) + 1,
  weight: `${(index % 6) + 1} lb`,
  equipped: index < EQUIPPED_STRESS_ITEMS.length,
  description: index % 6 === 0 ? LONG_ITEM_DESCRIPTION : ''
}));

const magicItems = Array.from({ length: 24 }, (_, index) => ({
  name: `Enchanted Relic ${String(index + 1).padStart(2, '0')}`,
  type: index % 3 === 0 ? 'wondrous item' : 'magic item',
  rarity: ['uncommon', 'rare', 'very rare'][index % 3],
  magical: true,
  requiresAttunement: index % 2 === 0,
  attuned: index < 3,
  equipped: index < 2,
  quantity: 1,
  weight: `${(index % 3) + 1} lb`,
  description: `${LONG_ITEM_DESCRIPTION} Relic sequence ${index + 1} preserves its complete effect for the printed record.`
}));

export const CHARACTER_PRINT_FIXTURE = {
  id: 'character-print-stress-fixture',
  name: 'Kerrigan Vale',
  level: 20,
  class: 'Rogue',
  subclass: 'Thief',
  classes: [
    { name: 'Rogue', subclass: 'Thief', level: 12, hitDie: 'd8' },
    { name: 'Wizard', subclass: 'Illusionist', level: 8, hitDie: 'd6', spellcastingProgression: 'Full' }
  ],
  species: 'Human',
  background: 'Criminal Informant',
  alignment: 'Chaotic Good',
  experiencePoints: 355000,
  hp: 123,
  maxHp: 147,
  tempHp: 8,
  ac: 19,
  initiative: 9,
  speed: { walk: 30, climb: 30 },
  proficiencyBonus: 6,
  passivePerception: 24,
  size: 'Medium',
  hitDice: '20d8',
  inspiration: true,
  deathSaves: { successes: 2, failures: 1 },
  abilities: {
    strength: 10,
    dexterity: 20,
    constitution: 16,
    intelligence: 14,
    wisdom: 18,
    charisma: 13
  },
  savingThrows: { dexterity: true, intelligence: true },
  skills: {
    Acrobatics: 'proficient',
    'Sleight of Hand': 'expert',
    Stealth: 'expert',
    Investigation: 'proficient',
    Insight: 'proficient',
    Perception: 'expert',
    Deception: 'proficient',
    Persuasion: 'proficient'
  },
  proficiencies: {
    armor: ['Light Armor'],
    weapons: ['Simple Weapons', 'Hand Crossbows', 'Longswords', 'Rapiers', 'Shortswords'],
    tools: ["Thieves' Tools", 'Disguise Kit', 'Forgery Kit'],
    languages: ['Common', 'Elvish', "Thieves' Cant"]
  },
  equipment: {
    weapons: [
      { name: 'Moonsteel Rapier', type: 'weapon', equipped: true, attackBonus: 11, damage: '1d8+5 piercing', range: 'Reach 5 ft.', mastery: 'Vex', notes: 'Finesse' },
      { name: 'Hand Crossbow', type: 'weapon', equipped: true, attackBonus: 11, damage: '1d6+5 piercing', range: { normal: 30, long: 120 }, mastery: 'Vex', notes: 'Ammunition, light, loading' },
      { name: 'Silvered Dagger', type: 'weapon', equipped: true, attackBonus: 11, damage: '1d4+5 piercing', range: { normal: 20, long: 60 }, mastery: 'Nick', notes: 'Finesse, light, thrown' }
    ],
    items: [...equipmentItems, ...magicItems],
    currency: { pp: 14, gp: 2387, ep: 0, sp: 46, cp: 19 },
    valuables: [
      { name: 'Black opal signet', quantity: 1 },
      { name: 'Letters of marque', quantity: 2 }
    ]
  },
  classFeatures: Array.from({ length: 14 }, (_, index) => ({
    name: `Rogue Feature ${index + 1}`,
    description: `A complete class feature reference for play. ${LONG_ITEM_DESCRIPTION}`,
    source: { name: 'Rogue' }
  })),
  speciesTraits: [
    { name: 'Resourceful', description: 'Gain Heroic Inspiration whenever you finish a Long Rest.' },
    { name: 'Skillful', description: 'Gain proficiency in one skill of your choice.' }
  ],
  feats: [
    { name: 'Alert', description: 'Add your Proficiency Bonus to Initiative and exchange Initiative with a willing ally.' },
    { name: 'Skulker', description: 'Move through the shadows and exploit concealment without losing the complete rules reminder.' }
  ],
  personalityTraits: 'Never enter a room without identifying two exits and the most expensive object.',
  ideals: 'Information should move freely, especially when tyrants try to bury it.',
  bonds: 'The Lantern Network gave Kerrigan a second chance; every field agent is family.',
  flaws: 'A locked door is interpreted as a personal challenge, even when caution would be wiser.',
  backstory: 'Kerrigan rose from courier to master infiltrator, exposing conspiracies across three kingdoms while keeping an unusually meticulous field journal.',
  identifyingFeatures: 'A thin silver scar crosses the left eyebrow; the right glove conceals a clockwork lockpick brace.',
  allies: 'The Lantern Network, Captain Orra Venn, and archivists of the Free Athenaeum.',
  foes: 'The Ministry of Celestial Affairs and its masked intelligence director.',
  deedsAndTitles: 'Bearer of the Night Key; Liberator of Bellweather Prison; unofficial royal locksmith.',
  politicalInfluence: 'Trusted by reformist courtiers and several guild councils, but watched by the crown.',
  storageEquipment: 'A locked sea chest at the Lantern safehouse contains forged identities, winter gear, and archived evidence.',
  notes: 'Use this fixture to inspect headings, repeated table headers, continuation pages, and dense narrative wrapping.',
  minions: [
    { name: 'Pip', description: 'A clockwork sparrow used for short-range scouting and message delivery.' }
  ]
};

export const CHARACTER_PRINT_OPTIONS = {
  spellData: {
    ability: 'intelligence',
    attackBonus: 8,
    saveDC: 16,
    slots: { 1: { total: 4, used: 1 }, 2: { total: 3, used: 0 }, 3: { total: 2, used: 1 } },
    entries: Array.from({ length: 18 }, (_, index) => ({
      id: `stress-spell-${index}`,
      name: `Field Spell ${String(index + 1).padStart(2, '0')}`,
      level: index % 4,
      prepared: index % 3 !== 0,
      ritual: index % 5 === 0,
      concentration: index % 4 === 2,
      castingTime: '1 action',
      range: index % 2 === 0 ? '60 feet' : 'Self',
      components: 'V, S, M',
      material: index % 3 === 0 ? 'a marked brass token' : '',
      duration: index % 4 === 2 ? 'Concentration, up to 1 minute' : '1 minute',
      description: `${LONG_ITEM_DESCRIPTION} Spell sequence ${index + 1} remains paired with its summary row.`
    }))
  },
  actionEconomyViewModel: {
    hasContent: true,
    movementSummary: 'Walk 30 ft., climb 30 ft.',
    sections: [
      {
        key: 'actions',
        title: 'Actions',
        entries: Array.from({ length: 10 }, (_, index) => ({
          key: `action-${index}`,
          name: `Tactical Action ${index + 1}`,
          meta: ['Action', index % 2 === 0 ? 'Melee' : '60 ft.'],
          description: `Resolve the full tactical effect without truncation. ${LONG_ITEM_DESCRIPTION}`
        }))
      },
      {
        key: 'bonusActions',
        title: 'Bonus Actions',
        entries: [{ key: 'cunning-action', name: 'Cunning Action', meta: ['Bonus Action'], description: 'Dash, Disengage, or Hide.' }]
      },
      {
        key: 'reactions',
        title: 'Reactions',
        entries: [{ key: 'uncanny-dodge', name: 'Uncanny Dodge', meta: ['Reaction'], description: 'Halve the damage from an attack that hits you.' }]
      }
    ]
  }
};

export const MINIMAL_CHARACTER_PRINT_FIXTURE = {
  id: 'character-print-minimal-fixture',
  name: 'Rowan Ash',
  level: 1,
  class: 'Fighter',
  species: 'Human',
  background: 'Guard',
  hp: 12,
  maxHp: 12,
  ac: 16,
  speed: 30,
  abilities: {
    strength: 16,
    dexterity: 12,
    constitution: 14,
    intelligence: 10,
    wisdom: 11,
    charisma: 8
  },
  savingThrows: { strength: true, constitution: true },
  equipment: { weapons: [], armor: [], items: [], currency: {} }
};

export const MINIMAL_CHARACTER_PRINT_OPTIONS = {
  actionEconomyViewModel: { hasContent: false, movementSummary: '', sections: [] }
};

export const KERRIGAN_CHARACTER_PRINT_FIXTURE = {
  id: 'kerrigan-brynn-print-regression',
  name: 'Kerrigan Brynn',
  level: 5,
  class: 'Rogue',
  subclass: 'Arcane Trickster',
  species: 'Human',
  background: 'Criminal',
  hp: 31,
  maxHp: 38,
  ac: 15,
  speed: 30,
  proficiencyBonus: 3,
  classes: [{ name: 'Rogue', level: 5, hitDice: '1d8' }],
  hitDiceSpent: 0,
  abilities: {
    strength: 8,
    dexterity: 16,
    constitution: 14,
    intelligence: 12,
    wisdom: 12,
    charisma: 14
  },
  savingThrows: { dexterity: true, intelligence: true },
  skills: { Stealth: 'expert', Persuasion: 'proficient', Perception: 'proficient' },
  proficiencies: {
    armor: ['Light Armor'],
    weapons: ['Simple Weapons', 'Rapiers', 'Shortswords'],
    tools: ["Thieves' Tools"],
    languages: ['Common', "Thieves' Cant"]
  },
  equipment: {
    weapons: [
      { name: 'Dagger', type: 'weapon', equipped: true, mastery: 'Nick' },
      { name: 'Short Sword', type: 'weapon', equipped: true, reach: 0 }
    ],
    armor: [
      { name: 'Glamored Studded Leather Armor', type: 'armor', equipped: true, magical: true, rarity: 'rare' }
    ],
    tools: [{ name: "Thieves' Tools", type: 'tool' }],
    items: [
      { name: 'Brooch of Shielding', type: 'wondrous item', magical: true, rarity: 'uncommon', equipped: true },
      { name: 'Goggles of Night', type: 'wondrous item', magical: true, rarity: 'uncommon', equipped: true },
      { name: 'Manticore Statuette', type: 'wondrous item', magical: true },
      { name: 'Whisperwire Brooch', type: 'wondrous item', magical: true },
      { name: 'Househand Clasp', type: 'wondrous item', magical: true },
      { name: 'Shuttered Spark Beetle', type: 'wondrous item', magical: true },
      { name: 'Potion of Healing', type: 'potion', quantity: 2 }
    ]
  },
  classFeatures: [
    { name: 'Expertise', description: 'Double your Proficiency Bonus for the selected proficiencies.' },
    { name: 'Cunning Strike' },
    { name: 'Mage Hand Legerdemaine', description: 'Use Mage Hand for additional legerdemain tasks.' }
  ],
  features: [
    { name: 'Expertise', description: 'Duplicate compatibility entry.' },
    { name: 'Starter Character Generator', description: 'Internal source marker.' }
  ],
  feats: [
    { name: 'Skillful', description: 'Gain a selected skill proficiency.' },
    { name: 'Versatile', description: 'Gain a selected Origin feat.' },
    { name: 'Telekinetic', source: 'Level 4 feat' },
    { name: 'Telepathic', source: 'Story feat' }
  ],
  notes: 'Session notes and player reminders.'
};

export const KERRIGAN_CHARACTER_PRINT_OPTIONS = {
  spellData: {
    ability: 'intelligence',
    slots: { 1: { total: 4, used: 3 }, 2: { total: 2, used: 2 } },
    entries: [
      { name: 'Mage Hand', level: 0 },
      { name: 'Minor Illusion', level: 0 },
      { name: 'Shocking Grasp', level: 0 },
      { name: 'Charm Person', level: 1, prepared: true },
      { name: 'Detect Thoughts', level: 2 },
      { name: 'Invisibility', level: 2, prepared: true },
      { name: 'Misty Step', level: 2, source: 'Story boon' }
    ]
  }
};

const CharacterPrintTestPage = () => {
  const fixtureName = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('fixture')
    : '';
  const minimalFixture = fixtureName === 'minimal';
  const kerriganFixture = fixtureName === 'kerrigan';
  const model = useMemo(
    () => {
      const selectedCharacter = kerriganFixture
        ? KERRIGAN_CHARACTER_PRINT_FIXTURE
        : (minimalFixture ? MINIMAL_CHARACTER_PRINT_FIXTURE : CHARACTER_PRINT_FIXTURE);
      const selectedOptions = kerriganFixture
        ? KERRIGAN_CHARACTER_PRINT_OPTIONS
        : (minimalFixture ? MINIMAL_CHARACTER_PRINT_OPTIONS : CHARACTER_PRINT_OPTIONS);
      const options = kerriganFixture
        ? {
          ...selectedOptions,
          actionEconomyViewModel: buildCharacterActionEconomyViewModel({
            ...selectedCharacter,
            spells: selectedOptions.spellData
          })
        }
        : selectedOptions;
      return buildCharacterPrintModel(selectedCharacter, options);
    },
    [kerriganFixture, minimalFixture]
  );

  return (
    <>
      <div className="character-print-root character-print-root--preview">
        <CharacterPrintDocument model={model} />
      </div>
      <CharacterPrintPortal model={model} />
    </>
  );
};

export default CharacterPrintTestPage;
